/**
 * Canonical match between a schedule item and the viewer's confirmed
 * registrations. Used by every schedule render path (ScheduleItemDisplay,
 * CalendarEventCard via CalendarTimeGrid) so they can't drift.
 *
 * Registrations are bucketed by status so the UI can tell "registered" apart
 * from "waitlisted" (the viewer signed up but the slot was full). Matching
 * rules, in priority order:
 *   1. Slot match — when the schedule item is pinned to a specific bookable
 *      slot via `activity_time_slot_id`, only a registration for that exact
 *      slot counts. A registered slot wins over a waitlisted one. This handles
 *      the canonical 1:1 case (Sound Healing 8am → that exact slot).
 *   2. Activity-level fallback — when the schedule item links to an activity
 *      but isn't pinned to a specific slot (e.g. a tournament block over
 *      several tee times), any registration for that activity counts, again
 *      preferring registered over waitlisted. The admin opts into this by
 *      leaving `activity_time_slot_id` NULL on the schedule item.
 *   3. Otherwise — no match (static schedule item with no activity link).
 *
 * Returns `null` when `viewerRegistrations` is null (no logged-in attendee) so
 * the call site can use the same value to gate the entire affordance.
 */
import type { ScheduleItemRow } from "../types/db-types";

/**
 * The viewer's registration ids, bucketed by status, for matching against
 * schedule items. `null` (at the call sites) means no logged-in attendee.
 */
export interface ViewerRegistrationKeys {
  /** Slot ids the viewer holds a confirmed (`registered`) signup for. */
  slotIds: Set<number>;
  /** Activity ids the viewer holds a confirmed (`registered`) signup for. */
  activityIds: Set<number>;
  /** Slot ids the viewer is `waitlisted` for. */
  waitlistedSlotIds: Set<number>;
  /** Activity ids the viewer is `waitlisted` for. */
  waitlistedActivityIds: Set<number>;
}

/** A schedule item's registration state for the current viewer. */
export type ScheduleRegistrationState = "registered" | "waitlisted" | null;

/**
 * Resolves whether the viewer is registered for, waitlisted for, or has no
 * signup against a schedule item.
 *
 * @param item - The schedule item (only its activity/slot links are read)
 * @param viewerRegistrations - The viewer's status-bucketed registrations, or `null` when there's no logged-in attendee
 * @returns `"registered"`, `"waitlisted"`, or `null` when there's no match / no attendee
 */
export function getScheduleItemRegistrationState(
  item: Pick<ScheduleItemRow, "activity_id" | "activity_time_slot_id">,
  viewerRegistrations: ViewerRegistrationKeys | null
): ScheduleRegistrationState {
  if (viewerRegistrations == null) return null;
  if (item.activity_time_slot_id != null) {
    if (viewerRegistrations.slotIds.has(item.activity_time_slot_id)) return "registered";
    if (viewerRegistrations.waitlistedSlotIds.has(item.activity_time_slot_id)) return "waitlisted";
    return null;
  }
  if (item.activity_id != null) {
    if (viewerRegistrations.activityIds.has(item.activity_id)) return "registered";
    if (viewerRegistrations.waitlistedActivityIds.has(item.activity_id)) return "waitlisted";
    return null;
  }
  return null;
}
